import { useState, useContext } from 'react';

import styled, { ThemeContext } from 'styled-components';

import { IconClipboardList } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { DeliveryState, OrderState } from 'data/__generated__';
import { StatusTag as OrderStatusTag } from 'pages/Orders/components/StatusTag';
import { StatusTag as DeliveryStatusTag } from 'pages/Deliveries/components/StatusTag';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { OrdersCalendarPopin } from 'pages/Orders/components/OrdersCalendarPopin';

// Row shape shared by the two "recent orders" sections (totem details & organization details).
// The organization one aggregates every totem of the organization, so its rows also carry the
// owning totem, displayed in an extra column.
export type RecentOrderRow = {
    _id: string;
    deliveryDate: string;
    deliveryState?: DeliveryState | null;
    state: OrderState;
    totemId?: string;
    totemFullname?: string | null;
};

export const RecentOrders = ({
    organizationId,
    totemId = null,
    orders,
    showTotemColumn = false,
}: {
    organizationId: string;
    // When set, the calendar popin opens pre-filtered on that totem.
    totemId?: string | null;
    orders: RecentOrderRow[];
    showTotemColumn?: boolean;
}) => {
    const theme = useContext(ThemeContext);
    const [isPopinOpen, setIsPopinOpen] = useState(false);

    function togglePopinOpen() {
        setIsPopinOpen(!isPopinOpen);
    }

    return (
        <Container>
            {isPopinOpen ? (
                <OrdersCalendarPopin
                    togglePopinOpen={togglePopinOpen}
                    organizationId={organizationId}
                    totemId={totemId}
                />
            ) : null}
            <Header>
                <TotemPrimaryButton type="button" onClick={togglePopinOpen}>
                    Voir le calendrier
                </TotemPrimaryButton>
            </Header>
            {orders.length ? (
                <OrdersTable>
                    <thead>
                        <tr>
                            <th>Date livraison</th>
                            {showTotemColumn ? <th>Totem</th> : null}
                            <th>Commande</th>
                            <th>Livraison</th>
                            <th aria-label="Détails" />
                        </tr>
                    </thead>
                    <tbody>
                        {orders.map((order) => {
                            const {
                                _id,
                                deliveryDate,
                                deliveryState,
                                state,
                                totemFullname,
                                totemId: orderTotemId,
                            } = order;
                            return (
                                <tr key={_id}>
                                    <td>{deliveryDate}</td>
                                    {showTotemColumn ? (
                                        <td>
                                            {orderTotemId ? (
                                                <TotemLink to={`/totem/${orderTotemId}`}>{totemFullname}</TotemLink>
                                            ) : (
                                                totemFullname || 'Ø'
                                            )}
                                        </td>
                                    ) : null}
                                    <td>
                                        <OrderStatusTag state={state} />
                                    </td>
                                    <td>{deliveryState ? <DeliveryStatusTag state={deliveryState} /> : 'Ø'}</td>
                                    <td>
                                        <Link to={`/order/${_id}`}>
                                            <DetailsIconContainer>
                                                <IconClipboardList size={22} color={theme?.textColor} />
                                            </DetailsIconContainer>
                                        </Link>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </OrdersTable>
            ) : (
                <EmptyLabel>Aucune commande récente</EmptyLabel>
            )}
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
    width: 100%;
    color: ${({ theme }) => theme.textColor};
`;

const Header = styled.div`
    display: flex;
    justify-content: flex-end;
`;

const OrdersTable = styled.table`
    width: 100%;
    border-collapse: collapse;

    th,
    td {
        padding: 10px 12px;
        text-align: left;
        vertical-align: middle;
    }

    thead th {
        font-size: 13px;
        font-weight: 600;
        color: ${({ theme }) => theme.infoTextColor};
        border-bottom: 1px solid ${({ theme }) => theme.lightBorderColor};
    }

    tbody tr:not(:last-child) td {
        border-bottom: 1px solid ${({ theme }) => theme.lightBorderColor};
    }

    tbody td:last-child {
        text-align: right;
    }
`;

const EmptyLabel = styled.div`
    color: ${({ theme }) => theme.infoTextColor};
`;

const TotemLink = styled(Link)`
    color: ${({ theme }) => theme.textColor};
    text-decoration: underline;
`;

const DetailsIconContainer = styled.div`
    display: inline-flex;
    align-items: center;
`;
